import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, CalendarOff, X, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

const CAPACITY_DEFAULTS: Record<string, number> = {
  full_time: 38,
  part_time: 25,
  contract: 10,
};

const TYPE_LABELS: Record<string, string> = {
  full_time: "Full-time",
  part_time: "Part-time",
  contract: "Contract",
};

const TYPE_COLORS: Record<string, string> = {
  full_time: "bg-primary/10 text-primary border-primary/30",
  part_time: "bg-accent/10 text-accent border-accent/30",
  contract: "bg-secondary text-secondary-foreground border-border",
};

export interface TeamMember {
  id: string;
  name: string;
  employment_type: "full_time" | "part_time" | "contract";
  capacity_hours: number;
  time_off: { id: string; start: string; end: string; reason: string }[];
}

interface Props {
  projectId: string;
  team: TeamMember[];
  onChange: (team: TeamMember[]) => void;
}

export default function TeamPanel({ projectId, team, onChange }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [addName, setAddName] = useState("");
  const [addType, setAddType] = useState<string>("full_time");
  const [addCapacity, setAddCapacity] = useState<number>(38);
  const [adding, setAdding] = useState(false);

  // Time off form per member
  const [toMemberId, setToMemberId] = useState<string | null>(null);
  const [toStart, setToStart] = useState("");
  const [toEnd, setToEnd] = useState("");
  const [toReason, setToReason] = useState("");

  const handleAddMember = async () => {
    if (!addName.trim()) return;
    setAdding(true);
    try {
      const { data } = await axios.post(
        `${BASE}/api/projects/${projectId}/team`,
        { name: addName.trim(), employment_type: addType, capacity_hours: addCapacity },
        { headers: authHeader }
      );
      onChange([...team, data]);
      setAddName("");
      setAddType("full_time");
      setAddCapacity(38);
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    await axios.delete(`${BASE}/api/projects/${projectId}/team/${memberId}`, { headers: authHeader });
    onChange(team.filter((m) => m.id !== memberId));
  };

  const handleUpdateCapacity = async (memberId: string, capacity_hours: number) => {
    const { data } = await axios.patch(
      `${BASE}/api/projects/${projectId}/team/${memberId}`,
      { capacity_hours },
      { headers: authHeader }
    );
    onChange(team.map((m) => (m.id === memberId ? { ...m, ...data } : m)));
  };

  const handleAddTimeOff = async (memberId: string) => {
    if (!toStart || !toEnd) return;
    const { data } = await axios.post(
      `${BASE}/api/projects/${projectId}/team/${memberId}/time-off`,
      { start: toStart, end: toEnd, reason: toReason },
      { headers: authHeader }
    );
    onChange(
      team.map((m) =>
        m.id === memberId ? { ...m, time_off: [...m.time_off, data] } : m
      )
    );
    setToMemberId(null);
    setToStart("");
    setToEnd("");
    setToReason("");
  };

  const handleRemoveTimeOff = async (memberId: string, toId: string) => {
    await axios.delete(
      `${BASE}/api/projects/${projectId}/team/${memberId}/time-off/${toId}`,
      { headers: authHeader }
    );
    onChange(
      team.map((m) =>
        m.id === memberId
          ? { ...m, time_off: m.time_off.filter((t) => t.id !== toId) }
          : m
      )
    );
  };

  return (
    <div className="p-4 space-y-5 max-w-2xl">
      <div>
        <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">Team</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{team.length} member{team.length !== 1 ? "s" : ""}</p>
      </div>

      {/* Add member form */}
      <div className="border border-border rounded-lg p-4 space-y-3 bg-secondary/20">
        <p className="text-xs font-semibold text-foreground">Add Team Member</p>
        <div className="flex gap-2">
          <Input
            value={addName}
            onChange={(e) => setAddName(e.target.value)}
            placeholder="Full name"
            className="flex-1"
            onKeyDown={(e) => e.key === "Enter" && handleAddMember()}
          />
          <Select
            value={addType}
            onValueChange={(v) => {
              setAddType(v);
              setAddCapacity(CAPACITY_DEFAULTS[v] ?? 38);
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="full_time">Full-time</SelectItem>
              <SelectItem value="part_time">Part-time</SelectItem>
              <SelectItem value="contract">Contract</SelectItem>
            </SelectContent>
          </Select>
          <Input
            type="number"
            value={addCapacity}
            onChange={(e) => setAddCapacity(+e.target.value)}
            className="w-20"
            min={1}
            max={80}
            title="Weekly capacity (hours)"
          />
          <Button onClick={handleAddMember} disabled={!addName.trim() || adding} className="gap-1.5 shrink-0">
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">Weekly capacity: {addCapacity} hrs</p>
      </div>

      {/* Team list */}
      {team.length === 0 ? (
        <div className="text-center py-12">
          <Users className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No team members yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {team.map((member) => (
            <div key={member.id} className="border border-border rounded-lg bg-card">
              {/* Member header */}
              <div className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-card-foreground">{member.name}</span>
                    <Badge variant="outline" className={`text-[10px] px-1.5 ${TYPE_COLORS[member.employment_type]}`}>
                      {TYPE_LABELS[member.employment_type]}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[11px] text-muted-foreground font-mono">
                      {member.capacity_hours} hrs/week capacity
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    value={member.capacity_hours}
                    onChange={(e) => handleUpdateCapacity(member.id, +e.target.value)}
                    className="w-16 h-7 text-xs text-center"
                    min={1}
                    max={80}
                    title="Weekly capacity (hours)"
                  />
                  <button
                    onClick={() => setToMemberId(toMemberId === member.id ? null : member.id)}
                    className="text-muted-foreground hover:text-foreground transition-colors p-1"
                    title="Add time off"
                  >
                    <CalendarOff className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => handleRemoveMember(member.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors p-1"
                    title="Remove member"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* Time off list */}
              {member.time_off.length > 0 && (
                <div className="border-t border-border px-4 py-2 space-y-1.5">
                  <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Time Off</p>
                  {member.time_off.map((to) => (
                    <div key={to.id} className="flex items-center gap-2 text-xs">
                      <span className="font-mono text-foreground">{to.start} → {to.end}</span>
                      {to.reason && <span className="text-muted-foreground">· {to.reason}</span>}
                      <button
                        onClick={() => handleRemoveTimeOff(member.id, to.id)}
                        className="ml-auto text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add time off form */}
              {toMemberId === member.id && (
                <div className="border-t border-border px-4 py-3 space-y-2 bg-secondary/20">
                  <p className="text-[11px] font-mono text-muted-foreground uppercase tracking-wider">Add Time Off</p>
                  <div className="flex gap-2 flex-wrap">
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-1">From</p>
                      <Input type="date" value={toStart} onChange={(e) => setToStart(e.target.value)} className="h-7 text-xs w-36" />
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground mb-1">To</p>
                      <Input type="date" value={toEnd} onChange={(e) => setToEnd(e.target.value)} className="h-7 text-xs w-36" />
                    </div>
                    <div className="flex-1 min-w-32">
                      <p className="text-[10px] text-muted-foreground mb-1">Reason (optional)</p>
                      <Input value={toReason} onChange={(e) => setToReason(e.target.value)} placeholder="e.g. vacation" className="h-7 text-xs" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="h-7 text-xs gap-1" onClick={() => handleAddTimeOff(member.id)} disabled={!toStart || !toEnd}>
                      <Plus className="h-3 w-3" /> Save
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setToMemberId(null)}>Cancel</Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
